import {
  RiFileTextLine,
  RiFlashlightLine,
  RiShoppingBagLine,
  RiStore2Line,
} from "@remixicon/react";
import { Link, Navigate, Outlet, Route } from "react-router-dom";

import { BreadcrumbInQueryCache } from "@/components/ConnectedBreadcrumbs/ConnectedBreadcrumbs";
import { BriefCrumb } from "@/pages/BriefBuilderAssistantPage/components/BriefCrumb.tsx";
import { ModificationRequestBreadcrumb } from "@/pages/ModificationRequestPage/ModificationRequestBreadcrumb";
import { OrderItemBreadcrumb } from "@/pages/OrderItemPage/OrderItemBreadcrumb";
import { OrderBreadcrumb } from "@/pages/OrdersPage/OrderBreadcrumb";
import { tolgee } from "@/providers/TranslationProvider";
import { DefaultRoute } from "./DefaultRoute";
import { ClientLayout } from "./layouts/ClientLayout";
import {
  AccountPage,
  BriefAssistantPage,
  CatalogMediumPage,
  CatalogPage,
  DashboardPage,
  InboxPage,
  ModificationRequestPage,
  OpenBriefPage,
  OrderItemPage,
  OrderPage,
  OrderSuccessPage,
  OrdersPage,
  SentBriefPage,
} from "./lazyPages";

export const clientRoutes = (
  <Route key="client-routes" path="/" element={<ClientLayout />}>
    <Route index element={<DefaultRoute />} />
    <Route
      handle={{
        crumb: () => (
          <Link to="/oms/dashboard" className="flex items-center gap-sp-base">
            <RiFlashlightLine className="size-sp-md" />
            {tolgee.t("navigation.dashboard")}
          </Link>
        ),
      }}
      path="dashboard"
      element={<DashboardPage />}
    />

    {/* Inbox Page */}
    <Route path="inbox" element={<InboxPage />} />

    {/* User Account */}
    <Route
      path="/account"
      element={<AccountPage />}
      handle={{
        crumb: () => (
          <Link to="/account">{tolgee.t("navigation.order.account")}</Link>
        ),
      }}
    />

    {/* Shop redirect to Catalog */}
    <Route path="/shop" element={<Navigate to="/catalog" replace />} />

    {/* Marketplace */}
    <Route
      path="/catalog"
      element={<Outlet />}
      handle={{
        crumb: () => (
          <Link to="/catalog" className="flex items-center gap-sp-base">
            <RiStore2Line className="size-sp-md" />
            {tolgee.t("navigation.order.catalog")}
          </Link>
        ),
      }}
    >
      <Route index element={<CatalogPage />} />
      <Route
        path="/catalog/:id"
        element={<Outlet />}
        loader={({ params }) => {
          return params;
        }}
        handle={{
          crumb: (data: { id: string }) => (
            <Link to={`/catalog/${data.id}`}>
              <BreadcrumbInQueryCache
                cacheKey={["catalogMedium", data.id]}
                name="data.medium.name"
              />
            </Link>
          ),
        }}
      >
        <Route index element={<CatalogMediumPage />} />
      </Route>
    </Route>

    {/* Client orders */}
    <Route
      path="/orders"
      element={<Outlet />}
      handle={{
        crumb: () => (
          <Link to="/orders" className="flex items-center gap-sp-base">
            <RiShoppingBagLine className="size-sp-md" />
            {tolgee.t("navigation.order.myOrders")}
          </Link>
        ),
      }}
    >
      <Route index element={<OrdersPage />} />
      <Route
        path="/orders/:orderId"
        element={<Outlet />}
        loader={({ params }) => {
          return params;
        }}
        handle={{
          crumb: (data: { orderId: string }) => {
            return <OrderBreadcrumb orderId={data.orderId} />;
          },
        }}
      >
        <Route index element={<OrderPage />} />
        <Route
          path="/orders/:orderId/order-items/:id"
          element={<Outlet />}
          loader={({ params }) => {
            return params;
          }}
          handle={{
            crumb: (data: { id: string; orderId: string }) => {
              return (
                <OrderItemBreadcrumb
                  orderId={data.orderId}
                  orderItemId={data.id}
                />
              );
            },
          }}
        >
          <Route index element={<OrderItemPage />} />
          <Route
            path="/orders/:orderId/order-items/:id/edit"
            element={<ModificationRequestPage />}
            loader={({ params }) => params}
            handle={{
              crumb: (data: { id: string; orderId: string }) => (
                <ModificationRequestBreadcrumb
                  orderId={data.orderId}
                  orderItemId={data.id}
                />
              ),
            }}
          />
        </Route>
      </Route>
      <Route path="/orders/success" element={<OrderSuccessPage />} />
    </Route>

    {/* Brief */}
    <Route
      path="/open-brief"
      element={<Outlet />}
      handle={{
        crumb: () => (
          <Link to="/open-brief" className="flex items-center gap-sp-base">
            <RiFileTextLine className="size-sp-md" />
            {tolgee.t("navigation.openBrief")}
          </Link>
        ),
      }}
    >
      <Route index element={<OpenBriefPage />} />
      <Route
        path="assistant"
        element={<BriefAssistantPage />}
        handle={{
          crumb: () => (
            <Link to="/open-brief/assistant">
              {tolgee.t("briefBuilderAssistant.title")}
            </Link>
          ),
        }}
      />
      <Route
        path="assistant/:briefId"
        element={<BriefAssistantPage />}
        handle={{
          crumb: () => <BriefCrumb to="/open-brief/assistant/:briefId" />,
        }}
      />
      <Route
        path=":briefId"
        element={<SentBriefPage />}
        handle={{
          crumb: () => <BriefCrumb to="/open-brief/:briefId" />,
        }}
      />
    </Route>
  </Route>
);
